import { type Component, type JSX, ErrorBoundary as SolidErrorBoundary } from "solid-js";
import { captureException } from "~/solidJs/shared/helpers/sentry";
import { showToastMessage, ToastMessageTypes } from "~/solidJs/widgets/WorkspaceToast/toaster";

interface ErrorBoundaryProps {
	children: JSX.Element;
	onError?: (error: Error) => void;
}

/**
 * Error Boundary component with Sentry integration
 * Catches errors in child components and shows toast notification
 */
export const ErrorBoundary: Component<ErrorBoundaryProps> = (props) => {
	const handleError = (error: Error, _reset: () => void) => {
		// Send to Sentry
		captureException(error, {
			errorBoundary: true,
			componentStack: error.stack
		});

		// Call custom error handler if provided
		props.onError?.(error);

		// Show toast notification
		showToastMessage({
			title: "Something went wrong",
			description: error.message || "An unexpected error occurred",
			type: ToastMessageTypes.Error,
			timeout: 5000
		});

		// Don't show fallback UI - return null to keep rendering children
		return null;
	};

	return <SolidErrorBoundary fallback={handleError}>{props.children}</SolidErrorBoundary>;
};
